import { useUIStore } from '@/stores/useUIStore'

type ToastOptions = {
  /** Override the default title shown above the message. */
  title?: string
  /** Keep the toast until manually dismissed. Overrides `duration`. Default: false. */
  persistent?: boolean
  /** Custom auto-dismiss duration in ms. Default: 5000 (7000 for errors). Ignored if `persistent: true`. */
  duration?: number
}

/**
 * Toast hook. All toasts auto-dismiss after 5s by default (7s for errors).
 * The dismiss timer pauses when the browser tab is hidden and resumes when visible.
 *
 * Usage:
 *   toast.success('Saved')                              // default 5s
 *   toast.success('Saved', { duration: 2000 })          // custom 2s
 *   toast.error('Critical failure', { persistent: true }) // stays until dismissed
 */
export function useToast() {
  const addUIMessage = useUIStore(state => state.addUIMessage)

  return {
    success: (message: string, { title, persistent = false, duration }: ToastOptions = {}) =>
      addUIMessage({ type: 'success', title, message, persistent, duration }),

    error: (message: string, { title, persistent = false, duration }: ToastOptions = {}) =>
      addUIMessage({ type: 'error', title, message, persistent, duration }),

    warning: (message: string, { title, persistent = false, duration }: ToastOptions = {}) =>
      addUIMessage({ type: 'warning', title, message, persistent, duration }),

    info: (message: string, { title, persistent = false, duration }: ToastOptions = {}) =>
      addUIMessage({ type: 'info', title, message, persistent, duration }),
  }
}
